import { GTooltip } from 'components/basic-blocks'
import { AiOutlineQuestionCircle } from 'react-icons/ai'
import { GNewWindowedSelect } from 'components/basic-blocks/g-new-virtualized-select'
import {
	Platform,
	AccountSelectionState,
	GoogleAdsAccountBasicInfoDTO,
	FacebookAdsAccountBasicInfoDTO,
	BingAdsAccountBasicInfoDTO
} from './types'
import { FilterOptionOption } from 'react-select/dist/declarations/src/filters'
import { removeFadPrefix } from 'utils'

const GoogleAdsOptionLabel = ({
	opt
}: {
	opt: GoogleAdsAccountBasicInfoDTO
}) => {
	if (opt?.name) {
		return (
			<div className="flex items-center w-full gap-2">
				<span className="truncate">
					{opt.name} <span>({opt.account_number})</span>
				</span>
				{opt.manager && (
					<span className="flex-shrink-0 px-[6px] py-[2px] text-[11px] font-medium border bg-t-step-inactive border-brand text-[color:var(--brand-chip-ink,#554BFD)] rounded-full leading-[14px]">
						MCC
					</span>
				)}
			</div>
		)
	}
	return <>{opt?.account_number || ''}</>
}

interface AccountDropdownProps {
	platform: Platform
	accountOptions: any[]
	accountSelection: AccountSelectionState
	loading: boolean
	mccLink: any
	onChange: (selected: any) => void
}

export const AccountDropdown = ({
	platform,
	accountOptions,
	accountSelection,
	loading,
	mccLink,
	onChange
}: AccountDropdownProps) => {
	const getLabel = () => {
		if (platform === 'GADS') return 'Select an MCC or Google Ads account'
		if (platform === 'FADS') return 'Select a Meta Ads account to begin setup'
		return 'Select a Microsoft Ads account to begin setup'
	}

	if (platform === 'GADS') {
		return (
			<div className="flex flex-col gap-[6px]">
				<div className="flex items-center gap-2">
					<label className="text-[16px] leading-[22px] font-medium text-t-google-ad">
						{getLabel()}
					</label>
					<GTooltip
						content={
							<p>
								If you select an MCC account, another field will appear below,
								listing the PPC accounts within it.
								<br />
								<br />
								If you don’t see the Google Ads account you’re looking for, the
								email you authorized may not have access to it. Select another
								Google credential from the dropdown above to authorize a
								different email address.
							</p>
						}
						position="top"
						arrowClassName="w-0 h-0 absolute bottom-[0.5px] border-l-8 border-l-transparent border-r-8 border-r-transparent border-t-8 border-t-tooltip-caret"
						contentClassName="border-none mb-2 bg-t-smoke font-medium text-sm text-t-desc-gray"
					>
						<AiOutlineQuestionCircle className="text-t-gray-hold w-4 h-4" />
					</GTooltip>
				</div>
				<GNewWindowedSelect
					label=""
					options={(accountOptions || []) as GoogleAdsAccountBasicInfoDTO[]}
					value={accountSelection.selected as GoogleAdsAccountBasicInfoDTO}
					isDisabled={loading}
					onChange={onChange}
					placeholder="Search and select"
					isOptionSelected={(option) => {
						const opt = option as GoogleAdsAccountBasicInfoDTO
						const selected =
							accountSelection.selected as GoogleAdsAccountBasicInfoDTO
						return opt?.account_number === selected?.account_number
					}}
					formatOptionLabel={(option) => {
						const opt = option as GoogleAdsAccountBasicInfoDTO
						return <GoogleAdsOptionLabel opt={opt} />
					}}
					getOptionLabelComponent={(option) => {
						const opt = option as GoogleAdsAccountBasicInfoDTO
						return <GoogleAdsOptionLabel opt={opt} />
					}}
					filterOption={(option, query) => {
						if (!query) return true
						try {
							const opt =
								option as FilterOptionOption<GoogleAdsAccountBasicInfoDTO>
							const accountNumber =
								opt?.data?.account_number?.toLocaleLowerCase() || ''
							const name = opt?.data?.name?.toLocaleLowerCase() || ''
							const lowerQuery = query.toLocaleLowerCase()
							return (
								accountNumber.includes(lowerQuery) || name.includes(lowerQuery)
							)
						} catch (err) {
							console.error('Filter option error:', err)
							return true
						}
					}}
				/>
			</div>
		)
	}

	if (platform === 'FADS') {
		return (
			<div className="flex flex-col gap-[6px]">
				<div className="flex items-center gap-2">
					<label className="text-[16px] leading-[22px] font-medium text-t-google-ad">
						{getLabel()}
					</label>
					<GTooltip
						content={
							<p>Choose your advertising account to connect to this website.</p>
						}
						position="top"
						arrowClassName="w-0 h-0 absolute bottom-[0.5px] border-l-8 border-l-transparent border-r-8 border-r-transparent border-t-8 border-t-tooltip-caret"
						contentClassName="border-none mb-2 bg-t-smoke font-medium text-sm text-t-desc-gray"
					>
						<AiOutlineQuestionCircle className="text-t-gray-hold w-4 h-4" />
					</GTooltip>
				</div>
				<GNewWindowedSelect
					label=""
					options={(accountOptions || []) as FacebookAdsAccountBasicInfoDTO[]}
					value={accountSelection.selected as FacebookAdsAccountBasicInfoDTO}
					isDisabled={loading}
					onChange={onChange}
					getOptionLabel={(option) => {
						const opt = option as FacebookAdsAccountBasicInfoDTO
						return `${opt.name || ''} - ${removeFadPrefix(
							opt.account_number || ''
						)}`
					}}
					placeholder="Search and select"
					isOptionSelected={(option) => {
						const opt = option as FacebookAdsAccountBasicInfoDTO
						const selected =
							accountSelection.selected as FacebookAdsAccountBasicInfoDTO
						return (
							opt?.name === selected?.name ||
							opt?.account_number === selected?.account_number
						)
					}}
					formatOptionLabel={(option) => {
						const opt = option as FacebookAdsAccountBasicInfoDTO
						if (!opt) return ''
						return `${opt.name || ''} - ${removeFadPrefix(
							opt.account_number || ''
						)}`
					}}
					filterOption={(option, query) => {
						if (!query) return true
						try {
							const opt =
								option as FilterOptionOption<FacebookAdsAccountBasicInfoDTO>
							const data = opt?.data
							if (!data) return false
							const name = data.name?.toLocaleLowerCase() || ''
							const accountNumber =
								data.account_number?.toLocaleLowerCase() || ''
							const lowerQuery = query.toLocaleLowerCase()
							return (
								name.includes(lowerQuery) || accountNumber.includes(lowerQuery)
							)
						} catch (err) {
							console.error('Filter option error:', err)
							return true
						}
					}}
				/>
			</div>
		)
	}

	// BADS
	return (
		<div className="flex flex-col gap-[6px]">
			<div className="flex items-center gap-2">
				<label className="text-[16px] leading-[22px] font-medium text-t-google-ad">
					{getLabel()}
				</label>
				<GTooltip
					content={
						<p>Choose your advertising account to connect to this website.</p>
					}
					position="top"
					arrowClassName="w-0 h-0 absolute bottom-[0.5px] border-l-8 border-l-transparent border-r-8 border-r-transparent border-t-8 border-t-tooltip-caret"
					contentClassName="border-none mb-2 bg-t-smoke font-medium text-sm text-t-desc-gray"
				>
					<AiOutlineQuestionCircle className="text-t-gray-hold w-4 h-4" />
				</GTooltip>
			</div>
			<GNewWindowedSelect
				label=""
				options={(accountOptions || []) as BingAdsAccountBasicInfoDTO[]}
				value={accountSelection.selected as BingAdsAccountBasicInfoDTO}
				isDisabled={loading}
				onChange={onChange}
				placeholder="Search and select"
				isOptionSelected={(option) => {
					const opt = option as BingAdsAccountBasicInfoDTO
					const selected =
						accountSelection.selected as BingAdsAccountBasicInfoDTO
					return opt?.account_number === selected?.account_number
				}}
				formatOptionLabel={(option) => {
					const opt = option as BingAdsAccountBasicInfoDTO
					if (!opt) return ''
					return `${opt.account_number || ''} - ${opt.name || ''}`
				}}
				filterOption={(option, query) => {
					if (!query) return true
					try {
						const opt = option as FilterOptionOption<BingAdsAccountBasicInfoDTO>
						const data = opt?.data
						if (!data) return false
						const name = data.name?.toLocaleLowerCase() || ''
						const accountNumber = data.account_number?.toLocaleLowerCase() || ''
						const lowerQuery = query.toLocaleLowerCase()
						return (
							name.includes(lowerQuery) || accountNumber.includes(lowerQuery)
						)
					} catch (err) {
						console.error('Filter option error:', err)
						return true
					}
				}}
			/>
		</div>
	)
}
